import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Search, Sparkles, CheckCircle2, AlertTriangle, XCircle, Copy, RefreshCw,
  FileSearch, Bot, Globe, FileCode, Check, Save,
} from "lucide-react";
import { toast } from "sonner";

const DEFAULT_PATHS = ["/", "/services", "/about", "/coaching", "/contact", "/blog", "/book"];
const DEFAULT_BASE = "https://www.elevateglobalsolutions.net";

interface Issue { level: "error" | "warn" | "info"; message: string }
interface PageCheck {
  url: string; ok: boolean; status: number;
  title: string | null; titleLength: number;
  description: string | null; descriptionLength: number;
  canonical: string | null; h1Count: number;
  ogTitle: boolean; ogDescription: boolean; ogImage: boolean;
  jsonLdTypes: string[]; hasFaqSchema: boolean;
  hasViewport: boolean; hasLangAttr: boolean;
  imagesMissingAlt: number; wordCount: number; issues: Issue[];
}
interface AuditResponse { results: PageCheck[]; extras: Record<string, { ok: boolean; status: number; size: number }> }
interface Suggestion {
  titles?: string[]; descriptions?: string[];
  faqs?: { question: string; answer: string }[];
  keywords?: string[]; aiAnswer?: string;
  raw?: string; error?: string;
}

const sev = (level: Issue["level"]) =>
  level === "error" ? { c: "hsl(0 75% 55%)", Icon: XCircle }
  : level === "warn" ? { c: "hsl(38 92% 55%)", Icon: AlertTriangle }
  : { c: "hsl(207 68% 60%)", Icon: CheckCircle2 };

const copy = async (t: string) => {
  await navigator.clipboard.writeText(t);
  toast.success("Copied to clipboard");
};

const SeoAiPanel = () => {
  const [baseUrl, setBaseUrl] = useState(DEFAULT_BASE);
  const [pathsText, setPathsText] = useState(DEFAULT_PATHS.join("\n"));
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState<AuditResponse | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [purpose, setPurpose] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [pickedTitle, setPickedTitle] = useState<string | null>(null);
  const [pickedDesc, setPickedDesc] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [appliedPath, setAppliedPath] = useState<string | null>(null);

  const runAudit = async () => {
    setLoading(true);
    setSuggestion(null);
    const paths = pathsText.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 30);
    const { data, error } = await supabase.functions.invoke("seo-audit", {
      body: { baseUrl, paths },
    });
    setLoading(false);
    if (error) {
      toast.error("Audit failed", { description: error.message });
      return;
    }
    setAudit(data as AuditResponse);
    toast.success(`Audited ${(data as AuditResponse).results.length} pages`);
  };

  useEffect(() => { runAudit(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const selectedPage = audit?.results.find((r) => r.url === selected) ?? null;

  const generateSuggestions = async () => {
    if (!selectedPage) return;
    setSuggesting(true);
    setSuggestion(null);
    const { data, error } = await supabase.functions.invoke("seo-suggest", {
      body: {
        pageUrl: selectedPage.url,
        pageTitle: selectedPage.title,
        pageDescription: selectedPage.description,
        pagePurpose: purpose,
      },
    });
    setSuggesting(false);
    if (error) {
      toast.error("Generation failed", { description: error.message });
      return;
    }
    const s = data as Suggestion;
    setSuggestion(s);
    setPickedTitle(s.titles?.[0] ?? null);
    setPickedDesc(s.descriptions?.[0] ?? null);
    setAppliedPath(null);
  };

  const applyToPage = async () => {
    if (!selectedPage || !suggestion) return;
    const path = new URL(selectedPage.url).pathname || "/";
    setApplying(true);
    const { error } = await supabase.from("seo_overrides").upsert({
      path,
      title: pickedTitle,
      description: pickedDesc,
      keywords: suggestion.keywords ?? null,
      faqs: suggestion.faqs ?? null,
      ai_answer: suggestion.aiAnswer ?? null,
    }, { onConflict: "path" });
    setApplying(false);
    if (error) {
      toast.error("Couldn't save override", { description: error.message });
      return;
    }
    setAppliedPath(path);
    toast.success(`Applied to ${path}`, {
      description: "Live on the public site. Run audit again to verify.",
    });
  };

  const totals = audit ? audit.results.reduce(
    (acc, r) => {
      r.issues.forEach((i) => acc[i.level]++);
      return acc;
    }, { error: 0, warn: 0, info: 0 } as Record<Issue["level"], number>,
  ) : null;

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="rounded-xl border p-5" style={{ background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 16%)" }}>
        <div className="flex items-center gap-2 mb-4">
          <FileSearch size={18} style={{ color: "hsl(207 68% 60%)" }} />
          <h3 className="text-base font-semibold" style={{ color: "hsl(0 0% 92%)" }}>SEO & AI Search Audit</h3>
        </div>
        <div className="grid gap-3 md:grid-cols-[2fr_3fr]">
          <div className="space-y-1.5">
            <label className="text-xs font-medium" style={{ color: "hsl(0 0% 55%)" }}>Base URL</label>
            <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
              className="border-[hsl(0_0%_20%)] bg-[hsl(0_0%_6%)] text-[hsl(0_0%_90%)]" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium" style={{ color: "hsl(0 0% 55%)" }}>Paths (one per line, max 30)</label>
            <Textarea rows={3} value={pathsText} onChange={(e) => setPathsText(e.target.value)}
              className="border-[hsl(0_0%_20%)] bg-[hsl(0_0%_6%)] text-[hsl(0_0%_90%)] font-mono text-xs" />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs" style={{ color: "hsl(0 0% 45%)" }}>
            We fetch each page server-side and check title, meta, headings, Open Graph, JSON-LD, alt text, and content depth.
          </p>
          <Button size="sm" onClick={runAudit} disabled={loading}
            className="bg-[hsl(207_68%_50%)] hover:bg-[hsl(207_68%_45%)] text-white">
            {loading ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-2 h-3.5 w-3.5" />}
            {loading ? "Auditing..." : "Run audit"}
          </Button>
        </div>
      </div>

      {/* Summary */}
      {totals && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: "Errors", value: totals.error, color: "hsl(0 75% 55%)" },
            { label: "Warnings", value: totals.warn, color: "hsl(38 92% 55%)" },
            { label: "Info", value: totals.info, color: "hsl(207 68% 60%)" },
            { label: "Pages audited", value: audit?.results.length ?? 0, color: "hsl(142 70% 55%)" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border p-4"
              style={{ background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 16%)" }}>
              <p className="text-xs" style={{ color: "hsl(0 0% 50%)" }}>{s.label}</p>
              <p className="mt-1 text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* AI-ready files */}
      {audit && (
        <div className="rounded-xl border p-5" style={{ background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 16%)" }}>
          <div className="mb-3 flex items-center gap-2">
            <FileCode size={16} style={{ color: "hsl(142 70% 55%)" }} />
            <h3 className="text-sm font-semibold" style={{ color: "hsl(0 0% 92%)" }}>AI-readiness files</h3>
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            {Object.entries(audit.extras).map(([f, v]) => (
              <div key={f} className="flex items-center justify-between rounded-md border px-3 py-2"
                style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
                <span className="font-mono text-xs" style={{ color: "hsl(0 0% 75%)" }}>{f}</span>
                <Badge variant="outline" style={{
                  background: v.ok ? "hsl(142 70% 50% / 0.12)" : "hsl(0 75% 55% / 0.12)",
                  borderColor: v.ok ? "hsl(142 70% 50% / 0.4)" : "hsl(0 75% 55% / 0.4)",
                  color: v.ok ? "hsl(142 70% 65%)" : "hsl(0 75% 65%)",
                }}>
                  {v.ok ? `OK · ${(v.size / 1024).toFixed(1)}kb` : `Missing (${v.status})`}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pages list */}
      {audit && (
        <div className="grid gap-4 lg:grid-cols-[1fr_1.2fr]">
          <div className="space-y-2">
            <h3 className="text-sm font-semibold" style={{ color: "hsl(0 0% 80%)" }}>Pages</h3>
            {audit.results.map((r) => {
              const errs = r.issues.filter((i) => i.level === "error").length;
              const warns = r.issues.filter((i) => i.level === "warn").length;
              const isSel = selected === r.url;
              return (
                <button key={r.url} onClick={() => { setSelected(r.url); setSuggestion(null); }}
                  className="w-full rounded-lg border p-3 text-left transition-all"
                  style={{
                    background: isSel ? "hsl(207 68% 50% / 0.08)" : "hsl(0 0% 8%)",
                    borderColor: isSel ? "hsl(207 68% 50% / 0.45)" : "hsl(0 0% 16%)",
                  }}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium" style={{ color: "hsl(0 0% 90%)" }}>
                      {new URL(r.url).pathname || "/"}
                    </span>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {errs > 0 && (
                        <span className="rounded px-1.5 py-0.5 text-[10px] font-bold"
                          style={{ background: "hsl(0 75% 55% / 0.15)", color: "hsl(0 75% 65%)" }}>
                          {errs} err
                        </span>
                      )}
                      {warns > 0 && (
                        <span className="rounded px-1.5 py-0.5 text-[10px] font-bold"
                          style={{ background: "hsl(38 92% 55% / 0.15)", color: "hsl(38 92% 65%)" }}>
                          {warns} warn
                        </span>
                      )}
                      {errs === 0 && warns === 0 && (
                        <CheckCircle2 size={14} style={{ color: "hsl(142 70% 55%)" }} />
                      )}
                    </div>
                  </div>
                  <p className="mt-1 line-clamp-1 text-xs" style={{ color: "hsl(0 0% 50%)" }}>
                    {r.title || "(no title)"} · {r.wordCount} words
                  </p>
                </button>
              );
            })}
          </div>

          {/* Detail */}
          <div className="rounded-xl border p-5" style={{ background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 16%)" }}>
            {!selectedPage ? (
              <div className="flex h-full min-h-[300px] flex-col items-center justify-center text-center">
                <Globe size={28} style={{ color: "hsl(0 0% 30%)" }} />
                <p className="mt-3 text-sm" style={{ color: "hsl(0 0% 50%)" }}>
                  Select a page on the left to see findings and AI suggestions
                </p>
              </div>
            ) : (
              <div className="space-y-5">
                <div>
                  <p className="text-xs uppercase tracking-wide" style={{ color: "hsl(0 0% 45%)" }}>
                    {new URL(selectedPage.url).pathname}
                  </p>
                  <p className="mt-1 truncate text-sm font-semibold" style={{ color: "hsl(0 0% 92%)" }}>
                    {selectedPage.title || "(no title)"}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed" style={{ color: "hsl(0 0% 60%)" }}>
                    {selectedPage.description || "(no meta description)"}
                  </p>
                </div>

                {/* Quick stats */}
                <div className="grid grid-cols-3 gap-2 text-xs">
                  {[
                    { l: "Title len", v: selectedPage.titleLength },
                    { l: "Desc len", v: selectedPage.descriptionLength },
                    { l: "H1s", v: selectedPage.h1Count },
                    { l: "Words", v: selectedPage.wordCount },
                    { l: "Schemas", v: selectedPage.jsonLdTypes.length },
                    { l: "Imgs no alt", v: selectedPage.imagesMissingAlt },
                  ].map((s) => (
                    <div key={s.l} className="rounded-md border px-2 py-1.5"
                      style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
                      <p style={{ color: "hsl(0 0% 45%)" }}>{s.l}</p>
                      <p className="font-bold" style={{ color: "hsl(0 0% 88%)" }}>{s.v}</p>
                    </div>
                  ))}
                </div>

                {/* Schemas */}
                {selectedPage.jsonLdTypes.length > 0 && (
                  <div>
                    <p className="mb-1.5 text-xs font-medium" style={{ color: "hsl(0 0% 55%)" }}>Structured data</p>
                    <div className="flex flex-wrap gap-1.5">
                      {[...new Set(selectedPage.jsonLdTypes)].map((t) => (
                        <span key={t} className="rounded-full border px-2 py-0.5 text-[10px] font-medium"
                          style={{ background: "hsl(207 68% 50% / 0.1)", borderColor: "hsl(207 68% 50% / 0.3)", color: "hsl(207 68% 70%)" }}>
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Issues */}
                <div>
                  <p className="mb-2 text-xs font-medium" style={{ color: "hsl(0 0% 55%)" }}>
                    Findings ({selectedPage.issues.length})
                  </p>
                  {selectedPage.issues.length === 0 ? (
                    <div className="flex items-center gap-2 rounded-md border px-3 py-2"
                      style={{ background: "hsl(142 70% 50% / 0.08)", borderColor: "hsl(142 70% 50% / 0.3)" }}>
                      <CheckCircle2 size={14} style={{ color: "hsl(142 70% 60%)" }} />
                      <span className="text-xs" style={{ color: "hsl(142 70% 70%)" }}>All checks passed</span>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {selectedPage.issues.map((i, idx) => {
                        const s = sev(i.level);
                        return (
                          <div key={idx} className="flex items-start gap-2 rounded-md border px-3 py-2"
                            style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
                            <s.Icon size={13} style={{ color: s.c, marginTop: 2 }} />
                            <span className="text-xs leading-relaxed" style={{ color: "hsl(0 0% 78%)" }}>{i.message}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* AI Coach */}
                <div className="rounded-lg border p-4"
                  style={{ background: "hsl(207 68% 50% / 0.05)", borderColor: "hsl(207 68% 50% / 0.25)" }}>
                  <div className="mb-3 flex items-center gap-2">
                    <Bot size={15} style={{ color: "hsl(207 68% 65%)" }} />
                    <p className="text-sm font-semibold" style={{ color: "hsl(0 0% 92%)" }}>AI Content Coach</p>
                  </div>
                  <p className="mb-2 text-xs" style={{ color: "hsl(0 0% 60%)" }}>
                    Optional: add a sentence on this page's purpose for sharper suggestions.
                  </p>
                  <Textarea rows={2} value={purpose} onChange={(e) => setPurpose(e.target.value)}
                    placeholder="e.g. Land business owners looking to outsource cold calling..."
                    className="border-[hsl(0_0%_20%)] bg-[hsl(0_0%_6%)] text-[hsl(0_0%_90%)] text-xs" />
                  <Button size="sm" onClick={generateSuggestions} disabled={suggesting}
                    className="mt-3 w-full bg-gradient-to-r from-[hsl(207_68%_50%)] to-[hsl(207_68%_60%)] text-white hover:opacity-90">
                    {suggesting ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-2 h-3.5 w-3.5" />}
                    {suggesting ? "Generating..." : "Generate SEO + AI suggestions"}
                  </Button>

                  {suggestion && (
                    <div className="mt-4 space-y-4">
                      {suggestion.error && (
                        <p className="text-xs" style={{ color: "hsl(0 75% 65%)" }}>{suggestion.error}</p>
                      )}
                      {suggestion.titles && (
                        <PickList
                          label="Title options (click to choose)"
                          items={suggestion.titles}
                          picked={pickedTitle}
                          onPick={setPickedTitle}
                        />
                      )}
                      {suggestion.descriptions && (
                        <PickList
                          label="Meta description options (click to choose)"
                          items={suggestion.descriptions}
                          picked={pickedDesc}
                          onPick={setPickedDesc}
                        />
                      )}
                      {suggestion.keywords && suggestion.keywords.length > 0 && (
                        <div>
                          <p className="mb-1.5 text-xs font-semibold" style={{ color: "hsl(0 0% 70%)" }}>Target keywords</p>
                          <div className="flex flex-wrap gap-1.5">
                            {suggestion.keywords.map((k) => (
                              <span key={k} className="rounded-full border px-2 py-0.5 text-[10px]"
                                style={{ background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 80%)" }}>
                                {k}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {suggestion.aiAnswer && (
                        <div>
                          <div className="mb-1 flex items-center justify-between">
                            <p className="text-xs font-semibold" style={{ color: "hsl(0 0% 70%)" }}>
                              AI answer paragraph (for ChatGPT / Perplexity)
                            </p>
                            <button onClick={() => copy(suggestion.aiAnswer!)}
                              className="text-[10px] hover:text-white" style={{ color: "hsl(207 68% 60%)" }}>
                              <Copy size={11} className="inline mr-1" />Copy
                            </button>
                          </div>
                          <p className="rounded-md border p-2 text-xs leading-relaxed"
                            style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)", color: "hsl(0 0% 80%)" }}>
                            {suggestion.aiAnswer}
                          </p>
                        </div>
                      )}
                      {suggestion.faqs && suggestion.faqs.length > 0 && (
                        <div>
                          <p className="mb-1.5 text-xs font-semibold" style={{ color: "hsl(0 0% 70%)" }}>FAQ pairs (paste into page + add FAQPage schema)</p>
                          <div className="space-y-2">
                            {suggestion.faqs.map((f, i) => (
                              <div key={i} className="rounded-md border p-2"
                                style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-xs font-semibold" style={{ color: "hsl(0 0% 88%)" }}>{f.question}</p>
                                  <button onClick={() => copy(`Q: ${f.question}\nA: ${f.answer}`)}
                                    className="shrink-0 text-[10px]" style={{ color: "hsl(207 68% 60%)" }}>
                                    <Copy size={11} />
                                  </button>
                                </div>
                                <p className="mt-1 text-xs leading-relaxed" style={{ color: "hsl(0 0% 65%)" }}>{f.answer}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {suggestion.raw && (
                        <pre className="overflow-x-auto rounded-md border p-2 text-[10px]"
                          style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)", color: "hsl(0 0% 60%)" }}>
                          {suggestion.raw}
                        </pre>
                      )}

                      {/* Apply to page */}
                      <div className="rounded-md border p-3"
                        style={{ background: "hsl(142 70% 45% / 0.06)", borderColor: "hsl(142 70% 45% / 0.3)" }}>
                        <div className="mb-2 flex items-center justify-between">
                          <p className="text-xs font-semibold" style={{ color: "hsl(142 70% 70%)" }}>
                            Apply to {new URL(selectedPage.url).pathname || "/"}
                          </p>
                          {appliedPath === (new URL(selectedPage.url).pathname || "/") && (
                            <span className="flex items-center gap-1 text-[10px]" style={{ color: "hsl(142 70% 65%)" }}>
                              <Check size={11} /> Live
                            </span>
                          )}
                        </div>
                        <p className="mb-3 text-[11px] leading-relaxed" style={{ color: "hsl(0 0% 60%)" }}>
                          Saves the picked title, description, keywords, and FAQs as the live meta for this route.
                          FAQs are also injected as FAQPage schema. Overrides any per-page Helmet defaults.
                        </p>
                        <Button size="sm" onClick={applyToPage}
                          disabled={applying || (!pickedTitle && !pickedDesc && !suggestion.keywords?.length && !suggestion.faqs?.length)}
                          className="w-full bg-[hsl(142_70%_45%)] text-white hover:bg-[hsl(142_70%_40%)]">
                          {applying ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-2 h-3.5 w-3.5" />}
                          {applying ? "Saving..." : "Apply to page"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!audit && !loading && (
        <div className="rounded-xl border p-10 text-center"
          style={{ background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 16%)" }}>
          <Search size={28} className="mx-auto" style={{ color: "hsl(0 0% 35%)" }} />
          <p className="mt-3 text-sm" style={{ color: "hsl(0 0% 55%)" }}>Run an audit to begin.</p>
        </div>
      )}
    </div>
  );
};

const SuggestList = ({ label, items }: { label: string; items: string[] }) => (
  <div>
    <p className="mb-1.5 text-xs font-semibold" style={{ color: "hsl(0 0% 70%)" }}>{label}</p>
    <div className="space-y-1.5">
      {items.map((t, i) => (
        <div key={i} className="flex items-start justify-between gap-2 rounded-md border p-2"
          style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          <span className="text-xs leading-relaxed" style={{ color: "hsl(0 0% 85%)" }}>{t}</span>
          <button onClick={() => copy(t)} className="shrink-0 text-[10px]" style={{ color: "hsl(207 68% 60%)" }}>
            <Copy size={11} />
          </button>
        </div>
      ))}
    </div>
  </div>
);

const PickList = ({
  label, items, picked, onPick,
}: { label: string; items: string[]; picked: string | null; onPick: (v: string) => void }) => (
  <div>
    <p className="mb-1.5 text-xs font-semibold" style={{ color: "hsl(0 0% 70%)" }}>{label}</p>
    <div className="space-y-1.5">
      {items.map((t, i) => {
        const isPicked = picked === t;
        return (
          <div key={i}
            className="flex items-start justify-between gap-2 rounded-md border p-2 cursor-pointer transition-colors"
            onClick={() => onPick(t)}
            style={{
              background: isPicked ? "hsl(207 68% 50% / 0.12)" : "hsl(0 0% 6%)",
              borderColor: isPicked ? "hsl(207 68% 50% / 0.5)" : "hsl(0 0% 14%)",
            }}>
            <span className="text-xs leading-relaxed" style={{ color: "hsl(0 0% 88%)" }}>{t}</span>
            <div className="flex shrink-0 items-center gap-2">
              {isPicked && <Check size={12} style={{ color: "hsl(207 68% 65%)" }} />}
              <button onClick={(e) => { e.stopPropagation(); copy(t); }}
                className="text-[10px]" style={{ color: "hsl(207 68% 60%)" }}>
                <Copy size={11} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

export default SeoAiPanel;
